/**
 * First-run onboarding: whether it has been completed, and whether it is open.
 *
 * `apiboo.onboarding.v1 = { role, completedAt }` in localStorage. The
 * `apiboo.` prefix makes it device-global (storageNamespace.js), so signing
 * out does not re-offer the tour. Bumping the key's version re-offers it.
 *
 * The flow opens once: on a fresh install it starts at the terms (Welcome)
 * step, and it stays up until it is finished or skipped. Help → "Replay
 * onboarding" reopens it at the role step without touching the terms record.
 */
import { create } from 'zustand';
import { needsTermsAcceptance } from '../components/FirstRunConsent';

export const ONBOARDING_KEY = 'apiboo.onboarding.v1';

export function readOnboarding() {
  try {
    const raw = localStorage.getItem(ONBOARDING_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function isOnboardingComplete() {
  return Boolean(readOnboarding()?.completedAt);
}

export function markOnboardingComplete(role) {
  try {
    localStorage.setItem(
      ONBOARDING_KEY,
      JSON.stringify({ role: role || null, completedAt: new Date().toISOString() })
    );
  } catch {
    // Non-fatal: the tour may be offered again next launch.
  }
}

/**
 * Show the flow on launch? The terms screen is its first step, so an
 * unaccepted terms version always opens it; otherwise only a device that has
 * never finished (or skipped) the tour sees it.
 */
export function shouldOpenOnLaunch() {
  let terms = false;
  try {
    terms = needsTermsAcceptance();
  } catch {
    terms = false;
  }
  if (terms) return true;
  try {
    localStorage.getItem(ONBOARDING_KEY);
  } catch {
    return false; // storage unavailable: never block the app on a tour
  }
  return !isOnboardingComplete();
}

export const useOnboardingStore = create((set) => ({
  open: false,
  // 'welcome' | 'role' — where the flow starts when it opens.
  startAt: 'welcome',
  // Bumped on every open so the dialog starts fresh after a replay.
  session: 0,
  // True while the last step points at the rail "?" — the real button pulses.
  railHelpPulse: false,
  openOnboarding: (startAt = 'welcome') => set((s) => ({ open: true, startAt, session: s.session + 1 })),
  closeOnboarding: () => set({ open: false, railHelpPulse: false }),
  setRailHelpPulse: (railHelpPulse) => set({ railHelpPulse }),
}));

/** Help → "Replay onboarding". */
export function replayOnboarding() {
  useOnboardingStore.getState().openOnboarding('role');
}
